import { Download, RefreshCw, Share2, Star, Trash2 } from 'lucide-react';
import { Button } from '../../../../components/Button';
import { ShortcutButton } from '../../../../components/ShortcutButton';
import { usePermission } from '../../../../auth/AuthContext';
import type { RepositoryItemListItem } from '../repositoryTypes';

/**
 * The record-level action strip under a document's header — reference §14's chips in
 * `<ProfileHeaderShell>`'s `toolbar` slot.
 *
 * **Captured from GRC's DIM**, whose per-item actions are Download, Replace (Increment
 * Version), Share, Pin and Delete. The chips are this product's: `<ShortcutButton size="xs">`
 * is what the deviations table already names as the profile-toolbar size, at exactly §5's
 * 40px touch-target floor.
 *
 * **An action belongs to the thing it changes**, and all five of these act on the whole
 * record rather than on one card's fields — so they sit here, on screen for every panel,
 * rather than in a card's cog. That is the same reasoning that puts a lifecycle action in
 * `<ProfileBanner>`.
 *
 * **Each chip carries the key of what it DOES**, never the key of the page it sits on, and
 * hides without it. Disabling is for STATE — no file to download, a deleted record — and then
 * it says why, which `<ShortcutButton>` enforces by taking `disabledReason` rather than a bare
 * `disabled`.
 */
export function DocumentToolbar({
  document,
  pinned,
  onDownload,
  onReplace,
  onShare,
  onTogglePin,
  onDelete,
}: {
  document: RepositoryItemListItem;
  /** Whether THIS reader has pinned it. A pin is personal — see `RepositoryPin`. */
  pinned: boolean;

  /*
    **An absent callback removes its chip** — the `<EditableAvatar>` pattern. A chip that opens
    nothing is the dead affordance the hide-rule forbids, so one appears the moment it is passed
    a handler and nothing else about this strip changes.

    **SHARE landed on 19 Sep 2026 and is passed.** It carries `repository.share` rather than the
    update key beside it — a chip carries the key of what it DOES — and the dialog behind it
    resolves the other half, opening read-only for somebody who may see the grants and not
    change them. Replace landed on 18 Sep 2026.

    Do NOT reach for a `disabledReason` instead. Disabling is for state the reader can act on
    — no file, a deleted record — and "this is not built yet" is neither a state of the record
    nor something they can do anything about. It would also put our build order on their
    screen, which is the invented-terminology rule one step along.
  */
  onDownload: () => void;
  onReplace?: () => void;
  onShare?: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}) {
  const canDownload = usePermission('repository.download');
  const canUpdate = usePermission('repository.update');
  const canRead = usePermission('repository.read');
  const canDelete = usePermission('repository.delete');

  // Its OWN key, never the update key beside it: deciding who else can reach a document is a
  // different privilege from changing it, which is why `repository.share` exists.
  const canShare = usePermission('repository.share');

  /*
    A deleted record is read-only. Every write here is refused by the server on one anyway, so
    the chips say so rather than producing a rejection the reader could have been spared —
    disabled for STATE, with the reason, which is not the same as a permission gate.
  */
  const deleted = !document.isActive ? 'This document is deleted. Restore it first.' : null;

  const noFile = document.storedFileId === null ? 'This document has no file.' : null;

  return (
    <>
      {/*
        DOWNLOAD is the strip's PRIMARY action, and the only one drawn as a button. Product
        call, 18 Sep 2026.

        It is what a reader does with a document far more often than anything else, and the
        three beside it CHANGE the record where this one only reads it — so it takes the brand
        yellow, which this product reserves for the primary action of a surface. That is the
        same grant the Create button has on an index page, and it is the reason this is not a
        record's STATE wearing the brand colour, which the deviations table forbids.

        It sits INLINE with the chips and at their height for free: `<Button>` is `min-h-10`
        and `<ShortcutButton size="xs">` is `h-10`, so the shell's `flex items-center` row
        aligns them with no sizing here. Nothing is full width — a button spanning the strip
        read as a banner rather than as one action among four.

        It keeps its WORD where the chips lost theirs, and that is the point rather than an
        inconsistency: this one is a button and a button says what it does, where a 40px chip
        is read as an icon and explains itself on hover through the tooltip
        `<ShortcutButton>` makes mandatory.
      */}
      {canDownload ? (
        <Button
          id="repository-document-download"
          variant="primary"
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
          /*
            A plain `disabled` here, not `<ShortcutButton>`'s `disabledReason` — `<Button>` has
            no tooltip of its own, and the reason a document has no file is already on screen:
            the header's status pill reads "No file" and the Preview tab says so in a sentence.
          */
          disabled={noFile !== null}
        >
          Download
        </Button>
      ) : null}

      {canUpdate && onReplace ? (
        <ShortcutButton
          id="repository-document-replace"
          size="xs"
          icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
          label="Replace"
          /*
            Replacing a file is a new VERSION, not an overwrite — it archives what it
            supersedes and bumps the number. The tooltip says so, because "Replace" on its own
            reads as destructive and this is the opposite.
          */
          tooltip="Replace the file — archives the current version and issues the next"
          disabledReason={deleted ?? noFile}
          onClick={onReplace}
        />
      ) : null}

      {canShare && onShare ? (
        <ShortcutButton
          id="repository-document-share"
          size="xs"
          icon={<Share2 className="h-4 w-4" aria-hidden="true" />}
          label="Share"
          tooltip="Grant another reader access to this document"
          disabledReason={deleted}
          onClick={onShare}
        />
      ) : null}

      {/*
        PIN carries `repository.read`, which is the one key here that is not a write key — and
        deliberately. A pin is a bookmark on this reader's own view: it changes nothing anybody
        else sees, so gating it behind an update grant would stop a read-only reader
        bookmarking anything.
      */}
      {canRead ? (
        <ShortcutButton
          id="repository-document-pin"
          size="xs"
          tone={pinned ? 'primary' : 'default'}
          icon={
            <Star
              className={`h-4 w-4 ${pinned ? 'fill-current' : ''}`}
              aria-hidden="true"
            />
          }
          label={pinned ? 'Unpin' : 'Pin'}
          tooltip={pinned ? 'Remove from your pinned items' : 'Pin to your pinned items'}
          disabledReason={deleted}
          onClick={onTogglePin}
        />
      ) : null}

      {/*
        DELETE sits on the OUTER RIGHT, set apart from the four actions before it. Product
        call, 18 Sep 2026.

        It is the one destructive control on the strip, and the gap is the point: next to Pin
        it was one 40px chip away from an action a reader takes casually and often. This is the
        same separation a modal footer gives a destructive action, which CLAUDE.md sets out
        under "Modal footers are right-aligned" — the direction is mirrored because a footer's
        run ends on its primary and a toolbar's begins with one.

        The auto margin goes on a WRAPPER, not on the chip. `<ShortcutButton>` renders inside
        `<Tooltip>`'s own span, so that span is the flex item — a margin on the button would
        sit one element inside the thing that needs to move, and the component takes no
        `className` to put it there with anyway.

        `ml-auto` is the right mechanism HERE and is the forbidden one in a modal footer: that
        row carries `justify-end` for an auto margin to fight with, and this strip carries no
        `justify-*` at all.
      */}
      {canDelete ? (
        <div className="ml-auto">
          <ShortcutButton
            id="repository-document-delete"
            size="xs"
            tone="danger"
            icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
            label="Delete"
            tooltip="Delete this document. It can be restored."
            disabledReason={deleted}
            onClick={onDelete}
          />
        </div>
      ) : null}
    </>
  );
}
